import { Component, Watch } from 'vue-property-decorator'
import Vue from '@src/vue'
import ClubModel from '@src/data/club'
import Member from '@src/data/member'
import TrainingPlan, { TrainingDay } from '@src/data/training-plan'
import WorkoutData, { HolderType } from '@src/data/workout-data'
import { TrainingPlanService } from '@src/service/training-plan'
import { CreateElement } from 'vue'
import { Button } from '@src/elements/button/button'
import async from 'async'
import camelcaseKeys from 'camelcase-keys'
import { IntersectionsPopup } from '@src/components/intersections-popup/intersections-popup'
import { convertToYYYYMMDD } from '@src/util/date-format'
import { MemberProfile } from '@src/data/member-profile'
import SessionManager from '@src/lib/session_manager'
import { defaultTrainingPlanFilters, defaultWorkoutSelectorFilters } from '@src/state/dafaults'
import { Tooltip } from '@src/elements/tooltip/tooltip'

class ErrorResponse {
  errors: {
    intersections: string[]
  }

  constructor (attributes?) {
    Object.assign(this, attributes)
  }
}

export type TrainingDayDTO = {
  workout_id
  rest_days
}

export class TrainingPlanDTO {
  name: string
  holderId: number
  holderType: HolderType
  trainingDays: TrainingDayDTO[]

  constructor (attributes?) {
    Object.assign(this, attributes)
    this.holderType = HolderType.CLUB
  }
}

export class MemberTrainingPlanDTO extends TrainingPlanDTO {
  id: number
  startDate: string
  visible: boolean

  constructor (attributes?) {
    super()
    Object.assign(this, attributes)
    this.holderType = HolderType.MEMBER
  }
}

@Component({
  computed: {
    trainingDays () {
      return this.$store.state.new_training_plan.trainingDays || []
    },
    selectedWorkouts () {
      let selectedWorkouts = []
      this.$store.state.new_training_plan.trainingDays.forEach(day => {
        if (day.workoutAsArray.length > 0) {
          selectedWorkouts = selectedWorkouts.concat(day.workoutAsArray[0])
        }
      })
      return selectedWorkouts
    },
    member () {
      return this.$store.state.trainingMember
    },
    club () {
      return this.$store.state.club
    },
    selectedTrainingPlan () {
      return this.$store.state.new_training_plan.selectedTrainingPlan
    },
    trainingPlanName () {
      return this.$store.state.new_training_plan.selectedTrainingPlan.name
    },
    startDate () {
      return this.$store.state.activePlanStartDate
    }
  }
})
export class SaveTrainingPlan extends Vue {

  protected club: ClubModel

  protected selectedTrainingPlan

  protected member: Member | MemberProfile | null

  protected startDate: string

  private trainingDays: TrainingDay[]
  private selectedWorkouts: WorkoutData[]

  // member edit is allowed in the training plan builder, but not allowed if editing the member owned plan
  private allowMemberChange: boolean = true

  // training plan is member or club owned (value  to be used to determine the type of plan to be created);
  // if the plan is club owned, and a member is selected, another member owned plan needs to be created
  private holderType: HolderType = HolderType.CLUB

  // handle plan intersections
  private showIntersectionsPopup: boolean = false
  private intersections: TrainingPlan[] = []
  private overwritingTrainingPlan: MemberTrainingPlanDTO

  private trainingPlanService: TrainingPlanService = new TrainingPlanService()

  private trainingPlanName: string

  private saveInProgress: boolean = false
  private initialTrainingPlanName: string = ''
  private errorMessage: string = ''

  beforeMount () {
    if (this.member) {
      // set plan as member owned
      this.holderType = HolderType.MEMBER
      // if the member data is sent over by the trainings menu, deactivate the member edit
      this.allowMemberChange = false
    }
    this.initialTrainingPlanName = this.selectedTrainingPlan.name
  }

  @Watch('trainingDays', { deep: true })
  checkFirstDay () {
    this.errorMessage = this.trainingDays[0] && !this.trainingDays[0].workoutAsArray.length ? 'SAVE_ERROR.MANDATORY_FIRST_DAY' : ''
  }

  isButtonDisabled () {
    // mandatory name
    if (!this.trainingPlanName || this.trainingPlanName.trim() === '') return true
    // mandatory startDate if it's assigned to member
    if (!this.allowMemberChange && !this.startDate) return true
    // at least one selected WO
    if (!this.selectedWorkouts || (this.selectedWorkouts && this.selectedWorkouts.length === 0)) return true

    // mandatory workout in first day
    if (!this.trainingDays[0].workoutAsArray.length) return true

    if (this.saveInProgress) return true

    return false
  }

  render (h: CreateElement) {
    return <div>
      {this.errorMessage !== '' ?
        <Tooltip slot='topRight' class='save-error' content={this.$t(`PAGE.CONTENT.TRAINING_PLAN.TRAINING_PLAN_BUILDER.${this.errorMessage}`)}>
          {this.renderButton(h)}
        </Tooltip> :
        this.renderButton(h)
      }
      {this.showIntersectionsPopup ? <IntersectionsPopup show={true}
                                                         openInNewTab={true}
                                                         closeCallback={this.closeIntersectionsPopup}
                                                         member={this.member}
                                                         intersections={this.intersections}
                                                         overwritingTrainingPlan={this.overwritingTrainingPlan} />
        : null}
    </div>
  }

  renderButton (h: CreateElement) {
    return <Button type='elevated' class='save-btn' label={this.$t('SAVE')} click={this.save}
            icon={this.saveInProgress ? '../img/loader' : ''}
            disabled={this.isButtonDisabled()}
            />
  }

  save () {

    if (!this.saveInProgress) {
      this.saveInProgress = true

      if (this.selectedTrainingPlan && this.selectedTrainingPlan.id) {
        if (this.holderType === HolderType.MEMBER) {
          // edit a MEMBER training plan (active training plan of a member)
          async.waterfall([
            this.editActiveTrainingPlan,
            this.showSaveNotification
          ], () => {
            this.saveInProgress = false
          })

        } else {
          if (this.member) {
            // clone a CLUB training plan - because it was assigned to a member
            async.waterfall([
              this.activateTrainingPlan,
              this.showSaveNotification
            ], () => {
              this.saveInProgress = false
            })
          } else {
            // edit a CLUB training plan
            async.waterfall([
              this.editTrainingPlan,
              this.showSaveNotification
            ], () => {
              this.saveInProgress = false
            })
          }
        }
      } else {
        // CREATE or CLONE a club training plan; if assigning it to a member,
        // do just that and no longer create a club TP
        const execFunctions = [this.createTrainingPlan, this.activateTrainingPlan, this.showSaveNotification]
        this.member ? execFunctions.splice(0, 1) : execFunctions.splice(1, 1)

        async.waterfall([
          ...execFunctions
        ], () => {
          this.saveInProgress = false
        })
      }
    }
  }

  createTrainingPlan (done): Promise<any> | any {
    const trainingDays = this.formatTrainingDays(false)
    if (SessionManager.Instance.isManager() && this.holderType === HolderType.CLUB) {
      const trainingPlan = new TrainingPlanDTO({
        name: this.trainingPlanName,
        trainingDays: trainingDays,
        holderId: this.club.id,
        visible: true
      })

      return this.trainingPlanService.addPlan(trainingPlan)
        .then(() => {
          done(null, true, false)
          this.$store.dispatch('filterTrainingPlans', defaultTrainingPlanFilters)
        })
        .catch((error) => {
          console.log('cannot save training plan', error)
          this.showSaveFailedNotification()
          done(null, false, false)
        })
    } else {
      done(null, true, false)
    }
  }

  editTrainingPlan (done) {
    if (this.member) done(null)
    const trainingDays = this.formatTrainingDays(true)
    if (SessionManager.Instance.isManager()) {
      const trainingPlan = new TrainingPlanDTO({
        name: this.trainingPlanName,
        trainingDays: trainingDays,
        holderId: this.club.id,
        visible: true
      })

      this.trainingPlanService.editPlan(this.selectedTrainingPlan.id, trainingPlan)
        .then(() => done(null, true, false))
        .catch((error) => {
          console.log('cannot edit training plan', error)
          this.showSaveFailedNotification()
          done(null, false, false)
        })
    } else {
      done(null, true, false)
    }
  }

  /**
   * Build the list of workout days to be submitted to the server.
   * Each workout will have a subsequent number of rest days (meaning days without workout).
   *
   * e.g.:
   * - there can be a workout in day 1, then submit (workout_id: id, rest_days: 6)
   * - there can be a workout in day 2, then submit (workout_id: id, rest_days: 5)
   * - there can be a workout in day 1 and one in day 2, then submit (workout_id: id, rest_days: 0, workout_id: id, rest_days: 5)
   * @returns {any}
   */
  formatTrainingDays (persistDayId: boolean): TrainingDayDTO[] {
    let days: TrainingDayDTO[] = []
    let restDays = 0

    for (let index = 0; index < this.trainingDays.length; index++) {
      if (this.trainingDays[index].workoutAsArray.length === 1) {
        let dto: any = {
          workout_id: this.trainingDays[index].workoutAsArray[0].id,
          rest_days: restDays
        }
        if (persistDayId && this.trainingDays[index].id) {
          dto.id = this.trainingDays[index].id
        }

        days.push(dto)

        restDays = 0
      } else {
        restDays += 1
      }
      if (days[days.length - 1]) {
        days[days.length - 1].rest_days = restDays
      }
    }

    return days
  }

  async activateTrainingPlan (done) {
    let trainingDays = this.formatTrainingDays(false)
    if (this.member && this.startDate) {
      trainingDays = trainingDays.map(day => {
        return { rest_days: day.rest_days, workout_id: day.workout_id }
      })

      const memberTrainingPlan = TrainingPlanService.buildMemberOwnedPlan(this.trainingPlanName, convertToYYYYMMDD(this.startDate), trainingDays, this.member.id, true)
      this.trainingPlanService.addMemberOwnedPlan(memberTrainingPlan)
        .then((id: number) => {
          this.$store.dispatch('setCheckupTrainingPlan', id)
          done(null, true, true)
        })
        .catch(error => {
          this.handleIntersectionError(error, memberTrainingPlan)
          done(null, false, false)
        })
    } else {
      done(null, true, false)
    }
  }

  editActiveTrainingPlan (done) {
    if (this.member && this.selectedTrainingPlan) {
      const memberTrainingPlan = new MemberTrainingPlanDTO({
        id: this.selectedTrainingPlan.id,
        name: this.trainingPlanName,
        holderId: this.member.id,
        trainingDays: this.formatTrainingDays(true),
        startDate: convertToYYYYMMDD(this.startDate),
        visible: this.hasSomethingChangedInTrainingPlanComposition()
      })

      this.trainingPlanService.editActiveTrainingPlan(this.selectedTrainingPlan.id, memberTrainingPlan)
        .then(() => done(null, true, true))
        .catch((error) => {
          this.handleIntersectionError(error, memberTrainingPlan)
          done(null, false, false)
        })
    } else {
      done(null, false, false)
    }
  }

  hasSomethingChangedInTrainingPlanComposition () {
    // If edited training plan has visible true, even if something doesn't changes return true
    if (this.selectedTrainingPlan.visible) return true

    const newName = this.selectedTrainingPlan.name !== this.initialTrainingPlanName
    let newOrSwapedSelectedWorkouts
    if (this.selectedTrainingPlan.trainingDays) {
      newOrSwapedSelectedWorkouts = this.selectedTrainingPlan.trainingDays.map(trDay => trDay.workout ? trDay.workout.id : null).toString() !==
        this.selectedWorkouts.map(w => w.id).toString()
    } else {
      newOrSwapedSelectedWorkouts = true
    }
    return newName || newOrSwapedSelectedWorkouts
  }

  handleIntersectionError (error, memberTrainingPlan) {
    const assignError = new ErrorResponse(error && error.errorData)
    const intersections = assignError.errors && assignError.errors.intersections
    if (Array.isArray(intersections) && intersections.length > 0) {
      // if the plan intersects, show the popup to allow the user to decide the further actions
      this.intersections = intersections.map(trainingPlan => new TrainingPlan(camelcaseKeys(trainingPlan, { deep: true })))
      this.overwritingTrainingPlan = memberTrainingPlan
      this.showIntersectionsNotification()
    } else {
      this.showSaveFailedNotification()
    }
  }

  showSaveFailedNotification () {
    this.$store.dispatch('showToast', { message: this.$t('ERRORS.API.UNKNOWN'), fadeOut: false })
  }

  showIntersectionsNotification () {
    this.showIntersectionsPopup = true
  }

  showSaveNotification (savedSuccessfully, activatedSuccessfully, done) {
    // workout saved and assigned
    if (savedSuccessfully && activatedSuccessfully) {
      this.$store.dispatch('showToast',
        {
          message: this.$t('PAGE.CONTENT.TRAINING_PLAN.ASSIGN_NOTIFICATION', { name: this.member!.fullName }),
          fadeOut: true
        })
      // close the builder after the message is shown
      this.closeBuilder()
      return
    }

    // workout saved
    if (savedSuccessfully) {
      this.$store.dispatch('showToast',
        { message: this.$t('PAGE.CONTENT.TRAINING_PLAN.TRAINING_PLAN_BUILDER.SAVE_NOTIFICATION'), fadeOut: true })
      // close the builder after the message is shown
      this.closeBuilder()
      return
    }

    done()
  }

  closeBuilder () {
    this.$store.dispatch('filterWorkoutSelector', defaultWorkoutSelectorFilters())
    this.$store.dispatch('setTrainingPlanDays', [])
    this.$router.go(-1)
  }

  async closeIntersectionsPopup (successfulAssign: boolean) {
    if (successfulAssign) {
      this.showIntersectionsPopup = false
      this.intersections = []
      this.$store.dispatch('showToast', {
        message: this.$t('PAGE.CONTENT.TRAINING_PLAN.ASSIGN_NOTIFICATION', { name: (this.member ? this.member.fullName : '') }),
        fadeOut: true
      })
      this.closeBuilder()
    } else {
      this.showIntersectionsPopup = false
    }
  }
}
